<script setup>
import { computed, ref } from "vue";
import { Link, router } from "@inertiajs/vue3";
import { useAuthUser } from "@/composables/useAuthUser.js";
import { useModalogStore } from "@/stores/modalog.js";
import BrandThrobber from "@/Components/Brand/BrandThrobber.vue";
import FollowButton from "./FollowButton.vue";
import { resolveProfileTheme } from "./profileThemes.js";
import IconReorderList from "~/svg/icon-reorder-list.svg";
import { useInertiaAction } from "@/composables/useInertiaAction.js";
import { useClipboard } from "@vueuse/core";
import { useShareLink } from "@/composables/useShareLink";

const props = defineProps({
    owner: {
        type: Object,
        required: true,
    },
    // Visitors get a Follow button and a Bookmark List action in the rail.
    isOwner: {
        type: Boolean,
        default: true,
    },
    // Theme slug; falls back to the default theme (see profileThemes.js).
    theme: {
        type: String,
        default: undefined,
    },
    // Back-link overrides for pages a level deeper than the list (item
    // detail links back to its list rather than to all lists).
    backHref: {
        type: String,
        default: null,
    },
    backLabel: {
        type: String,
        default: null,
    },
    mobileBackLabel: {
        type: String,
        default: "All Lists",
    },
    // The content panel ends above something (promo banners, More Lists,
    // the card's bottom padding), so its bottom corners show.
    roundBottom: {
        type: Boolean,
        default: false,
    },
    // The giftlist being viewed; enables the visitor rail's Bookmark List
    // action (needs uuid + is_bookmark).
    list: {
        type: Object,
        default: null,
    },
    // Reordering needs something to reorder — the Reorder List rail link
    // only shows for lists with at least two gifts (GH-956).
    totalGifts: {
        type: Number,
        default: 0,
    },
});

const theme = computed(() => resolveProfileTheme(props.theme));

const ownerDisplayName = computed(() =>
    props.owner.is_organization
        ? props.owner.presentation_name
        : props.owner.full_name || props.owner.presentation_name,
);

// The avatar always leads to the owner's profile (BH-54), even on pages
// whose back link points elsewhere (item detail → its list).
const profileHref = computed(() =>
    route("user.giftlists", { username: props.owner.username }),
);
const profileLinkLabel = computed(
    () => `View ${ownerDisplayName.value}'s profile`,
);

const backHref = computed(() => props.backHref ?? profileHref.value);

const backLabel = computed(() => props.backLabel ?? "Back to Wish Lists");

// Fallback to an initial-letter disc when the avatar is missing or 404s.
const avatarFailed = ref(false);

// Same treatment as the item detail rail links (GH-835).
const railLinkClasses =
    "group inline-flex items-center gap-2.5 font-brand text-[15px] font-semibold text-brand-navy underline underline-offset-2 transition hover:text-brand-red";

// --- Owner rail: Edit List Details + Reorder List (GH-919/GH-920, per
//     Figma 224-2044) ---
const modalogStore = useModalogStore();

function editListDetails() {
    modalogStore.showModalog({
        type: "ModalogCreateListBrand",
        props: {
            isEdit: true,
            list: props.list,
            listType: props.list.type,
        },
    });
}

function reorderList() {
    router.get(route("list.reorder", { giftlist: props.list.uuid }));
}

// --- Copy Link (owner + visitor rails, BH-49) ---
// Same glyph as the item detail rail's Copy Link.
const COPY_PATH =
    "M9.88235 0H3.95294C3.22824 0 2.63529 0.592941 2.63529 1.31765V9.22353C2.63529 9.94824 3.22824 10.5412 3.95294 10.5412H9.88235C10.6071 10.5412 11.2 9.94824 11.2 9.22353V1.31765C11.2 0.592941 10.6071 0 9.88235 0ZM9.88235 9.22353H3.95294V1.31765H9.88235V9.22353ZM0 8.56471V7.24706H1.31765V8.56471H0ZM0 4.94118H1.31765V6.25882H0V4.94118ZM4.61176 11.8588H5.92941V13.1765H4.61176V11.8588ZM0 10.8706V9.55294H1.31765V10.8706H0ZM1.31765 13.1765C0.592941 13.1765 0 12.5835 0 11.8588H1.31765V13.1765ZM3.62353 13.1765H2.30588V11.8588H3.62353V13.1765ZM6.91765 13.1765V11.8588H8.23529C8.23529 12.5835 7.64235 13.1765 6.91765 13.1765ZM1.31765 2.63529V3.95294H0C0 3.22824 0.592941 2.63529 1.31765 2.63529Z";

const listUrl = computed(
    () => props.list?.open_graph?.url ?? props.list?.url ?? null,
);
const { copy: copyToClipboard, copied: linkCopied } = useClipboard({
    source: listUrl,
});
const { shareLink } = useShareLink();

// Tag the link so a signup through it is attributed back (GH-1192). Copy
// synchronously: shareLink() reports the token without awaiting.
function copyListLink() {
    if (!listUrl.value) {
        return;
    }

    copyToClipboard(
        shareLink(listUrl.value, { type: "giftlist", uuid: props.list.uuid }),
    );
}

// --- Bookmark List (visitor rail) ---
const authUser = useAuthUser();
const { busy: bookmarkBusy, run: runBookmark } = useInertiaAction();

// Present-progressive while the request runs (GH-987): Save List →
// Saving List, Remove Saved List → Removing Saved List.
const bookmarkLabel = computed(() => {
    if (bookmarkBusy.value) {
        return props.list?.is_bookmark ? "Removing Saved List" : "Saving List";
    }

    return props.list?.is_bookmark ? "Remove Saved List" : "Save List";
});

async function toggleBookmark() {
    if (!authUser.value) {
        router.get(route("login"));

        return;
    }

    if (bookmarkBusy.value || !props.list) {
        return;
    }

    // Busy spans the request and the reload (GH-1159).
    await runBookmark(() =>
        props.list.is_bookmark
            ? axios.delete(
                  route("bookmark.destroy", { giftlist: props.list.uuid }),
              )
            : axios.post(
                  route("bookmark.create", { giftlist: props.list.uuid }),
              ),
    );
}
</script>

<template>
    <section class="relative">
        <!-- Desktop: gradient band behind the rail and the panel's top edge -->
        <div
            class="pointer-events-none absolute inset-x-0 top-0 h-[271px] max-lg:hidden"
            :style="{ backgroundImage: theme.gradient }"
        ></div>

        <!-- Mobile band: back link + "wish list for" + avatar -->
        <div
            class="relative rounded-b-[10px] lg:hidden"
            :style="{ backgroundImage: theme.gradient }"
        >
            <div class="h-1.5" :style="{ background: theme.wedge }"></div>
            <div class="flex items-center justify-between gap-4 px-4 py-4">
                <Link
                    :href="backHref"
                    class="inline-flex items-center gap-2 font-brand text-[15px] font-semibold text-white underline underline-offset-2 hover:text-white"
                >
                    <!-- Red rounded triangle, matching the desktop rail's
                         back link (GH-1097) -->
                    <svg
                        viewBox="0 -0.2 8 11.6"
                        class="h-[11px] w-2 shrink-0 text-brand-red"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            d="M0.731 6.79C0 6.21 0 5.1 0.731 4.52L5.649 0.615C6.599 -0.139 8 0.537 8 1.751L8 9.559C8 10.772 6.599 11.449 5.649 10.694Z"
                        />
                    </svg>
                    {{ mobileBackLabel }}
                </Link>

                <div class="flex min-w-0 items-center gap-3">
                    <div class="min-w-0 text-right uppercase text-white">
                        <p
                            class="font-brand-display text-[14px] font-medium italic leading-[1.2] opacity-60"
                        >
                            Wish List For
                        </p>
                        <p
                            class="truncate font-brand-display text-[20px] font-black italic leading-[1.2]"
                        >
                            {{ ownerDisplayName }}
                        </p>
                    </div>
                    <Link
                        :href="profileHref"
                        class="shrink-0"
                        :aria-label="profileLinkLabel"
                        data-test="owner-avatar-link"
                    >
                        <img
                            v-if="owner.avatar?.profile_photo && !avatarFailed"
                            :src="owner.avatar.profile_photo"
                            :alt="ownerDisplayName"
                            class="size-[45px] rounded-full border-[3px] border-solid border-white object-cover"
                            @error="avatarFailed = true"
                        />
                        <div
                            v-else
                            class="grid size-[45px] place-items-center rounded-full border-[3px] border-solid border-white bg-white/25 font-brand-display text-[20px] font-black uppercase text-white"
                            aria-hidden="true"
                        >
                            {{ ownerDisplayName?.charAt(0) }}
                        </div>
                    </Link>
                </div>
            </div>

            <!-- Item detail drops its product image here, inside the band -->
            <slot name="mobile-band" />
        </div>

        <!-- Mobile action bar (GH-1057): the rail is hidden below lg, which
             left owners with no Edit/Reorder and visitors with no Save List
             on phones. Pages that replace the rail (item detail) bring their
             own mobile actions, so this bar only backs the default rail. -->
        <div
            v-if="!$slots.rail && list"
            class="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-solid border-black/10 bg-white px-4 py-3.5 lg:hidden"
        >
            <template v-if="isOwner">
                <button
                    type="button"
                    :class="railLinkClasses"
                    @click="editListDetails"
                >
                    <svg
                        viewBox="0 0 10 10"
                        class="size-3 text-[#88959E] transition group-hover:text-brand-red"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            d="M6.58089 1.09767L7.35833 0.320225C7.45964 0.218715 7.57996 0.138181 7.71243 0.0832325C7.84489 0.0282839 7.98689 0 8.1303 0C8.27371 0 8.41571 0.0282839 8.54818 0.0832325C8.68064 0.138181 8.80097 0.218715 8.90227 0.320225L9.67972 1.09767C10.1068 1.52472 10.1068 2.21457 9.67972 2.64162L8.90227 3.41907L6.58089 1.09767ZM5.80345 1.87512L0 7.67861V10H2.32138L8.12483 4.19651L5.80345 1.87512Z"
                        />
                    </svg>
                    Edit List Details
                </button>
                <button
                    v-if="totalGifts >= 2"
                    type="button"
                    :class="railLinkClasses"
                    @click="reorderList"
                >
                    <IconReorderList
                        class="h-[15px] w-[13px] shrink-0 text-[#88959E] transition group-hover:text-brand-red"
                        aria-hidden="true"
                    />
                    Reorder List
                </button>
            </template>
            <button
                v-else
                type="button"
                :class="railLinkClasses"
                :disabled="bookmarkBusy"
                @click="toggleBookmark"
            >
                <svg
                    viewBox="0 0 12 15"
                    class="h-[14px] w-3 text-[#88959E] transition group-hover:text-brand-red"
                    :fill="list.is_bookmark ? 'currentColor' : 'none'"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path d="M1 1h10v13L6 10.5 1 14V1z" />
                </svg>
                {{ bookmarkLabel }}
                <BrandThrobber v-if="bookmarkBusy" variant="mono" />
            </button>
            <button
                type="button"
                :class="railLinkClasses"
                data-test="copy-list-link"
                @click="copyListLink"
            >
                <svg
                    viewBox="0 0 12 14"
                    class="h-[14px] w-3 text-[#88959E] transition group-hover:text-brand-red"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path :d="COPY_PATH" />
                </svg>
                {{ linkCopied ? "Link Copied!" : "Copy Link" }}
            </button>
        </div>

        <div
            class="relative lg:grid lg:grid-cols-[282px_minmax(0,1fr)] lg:pr-[35px]"
        >
            <!-- Desktop rail: avatar + owner name inside the band, back link below -->
            <div class="flex-col items-center pt-[35px] max-lg:hidden lg:flex">
                <Link
                    :href="profileHref"
                    :aria-label="profileLinkLabel"
                    data-test="owner-avatar-link"
                >
                    <img
                        v-if="owner.avatar?.profile_photo && !avatarFailed"
                        :src="owner.avatar.profile_photo"
                        :alt="ownerDisplayName"
                        class="size-[150px] rounded-full border-[10px] border-solid border-white object-cover"
                        @error="avatarFailed = true"
                    />
                    <div
                        v-else
                        class="grid size-[150px] place-items-center rounded-full border-[10px] border-solid border-white bg-white/25 font-brand-display text-[64px] font-black uppercase text-white"
                        aria-hidden="true"
                    >
                        {{ ownerDisplayName?.charAt(0) }}
                    </div>
                </Link>

                <p
                    class="mt-3 font-brand-display text-[15px] font-medium italic uppercase leading-[1.2] text-white opacity-60"
                >
                    Wish List For
                </p>
                <p
                    class="max-w-full truncate px-3 text-center font-brand-display text-[24px] font-black italic uppercase leading-[1.2] text-white"
                >
                    {{ ownerDisplayName }}
                </p>

                <!-- The name strip ends 27px above the band's bottom edge, so
                     32px + 27px places the button 32px below the band — same
                     offset trick as the item detail rail (the name truncates,
                     never wraps, so the offset is stable). -->
                <FollowButton
                    v-if="!isOwner"
                    :owner="owner"
                    class="mt-[59px] w-[210px]"
                />

                <!-- Pages needing a custom rail (item detail's action links)
                     replace the default nav entirely -->
                <slot name="rail">
                    <div class="w-full">
                        <div
                            class="flex items-center px-[35px] pb-8"
                            :class="isOwner ? 'pt-[59px]' : 'pt-8'"
                        >
                            <Link :href="backHref" :class="railLinkClasses">
                                <!-- Red rounded triangle per Figma (deliberately
                                     breaks the currentColor pattern) -->
                                <svg
                                    viewBox="0 -0.2 8 11.6"
                                    class="h-2 w-[6px] text-brand-red"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M0.731 6.79C0 6.21 0 5.1 0.731 4.52L5.649 0.615C6.599 -0.139 8 0.537 8 1.751L8 9.559C8 10.772 6.599 11.449 5.649 10.694Z"
                                    />
                                </svg>
                                {{ backLabel }}
                            </Link>
                        </div>
                        <!-- Owner actions (per Figma 224-2044) -->
                        <div
                            v-if="isOwner && list"
                            class="flex flex-col items-start gap-5 border-y border-solid border-black/10 px-[35px] py-7"
                        >
                            <button
                                type="button"
                                :class="railLinkClasses"
                                @click="editListDetails"
                            >
                                <svg
                                    viewBox="0 0 10 10"
                                    class="size-3 text-[#88959E] transition group-hover:text-brand-red"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M6.58089 1.09767L7.35833 0.320225C7.45964 0.218715 7.57996 0.138181 7.71243 0.0832325C7.84489 0.0282839 7.98689 0 8.1303 0C8.27371 0 8.41571 0.0282839 8.54818 0.0832325C8.68064 0.138181 8.80097 0.218715 8.90227 0.320225L9.67972 1.09767C10.1068 1.52472 10.1068 2.21457 9.67972 2.64162L8.90227 3.41907L6.58089 1.09767ZM5.80345 1.87512L0 7.67861V10H2.32138L8.12483 4.19651L5.80345 1.87512Z"
                                    />
                                </svg>
                                Edit List Details
                            </button>
                            <button
                                v-if="totalGifts >= 2"
                                type="button"
                                :class="railLinkClasses"
                                @click="reorderList"
                            >
                                <!-- Exported from Figma 224-2276 -->
                                <IconReorderList
                                    class="h-[15px] w-[13px] shrink-0 text-[#88959E] transition group-hover:text-brand-red"
                                    aria-hidden="true"
                                />
                                Reorder List
                            </button>
                            <button
                                type="button"
                                :class="railLinkClasses"
                                data-test="copy-list-link"
                                @click="copyListLink"
                            >
                                <svg
                                    viewBox="0 0 12 14"
                                    class="h-[14px] w-3 text-[#88959E] transition group-hover:text-brand-red"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path :d="COPY_PATH" />
                                </svg>
                                {{ linkCopied ? "Link Copied!" : "Copy Link" }}
                            </button>
                        </div>

                        <div
                            v-if="!isOwner && list"
                            class="flex flex-col items-start gap-5 border-y border-solid border-black/10 px-[35px] py-7"
                        >
                            <button
                                type="button"
                                :class="railLinkClasses"
                                :disabled="bookmarkBusy"
                                @click="toggleBookmark"
                            >
                                <svg
                                    viewBox="0 0 12 15"
                                    class="h-[14px] w-3 text-[#88959E] transition group-hover:text-brand-red"
                                    :fill="
                                        list.is_bookmark
                                            ? 'currentColor'
                                            : 'none'
                                    "
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                    stroke-linejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M1 1h10v13L6 10.5 1 14V1z" />
                                </svg>
                                <!-- is_bookmark doesn't flip until the reload
                                     lands, so the in-flight label reads off
                                     the pre-toggle state. -->
                                {{ bookmarkLabel }}
                                <BrandThrobber
                                    v-if="bookmarkBusy"
                                    variant="mono"
                                />
                            </button>
                            <button
                                type="button"
                                :class="railLinkClasses"
                                data-test="copy-list-link"
                                @click="copyListLink"
                            >
                                <svg
                                    viewBox="0 0 12 14"
                                    class="h-[14px] w-3 text-[#88959E] transition group-hover:text-brand-red"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path :d="COPY_PATH" />
                                </svg>
                                {{ linkCopied ? "Link Copied!" : "Copy Link" }}
                            </button>
                        </div>
                    </div>
                </slot>
            </div>

            <!-- Content panel: masthead + gift grid live here -->
            <div
                class="bg-brand-cream lg:mt-[35px] lg:rounded-t-[5px]"
                :class="{ 'rounded-b-[5px]': roundBottom }"
            >
                <slot />
            </div>
        </div>
    </section>
</template>
